// Client-side (offline) comparison engine.
// Renders PDF pages via pdf.js and decodes TIFF frames via UTIF, then performs
// pixel-by-pixel comparison on <canvas> ImageData.
//
// Memory strategy:
//   - Pages are decoded lazily, one pair at a time. We never hold every page
//     of every file in RAM.
//   - Pages that exceed MAX_PIXELS_PER_PAGE are proportionally downscaled to
//     stay within the browser's array-buffer limits.
//   - Raw decoded data is released after each page compare.
//
// Output shape mirrors the previous response shape so the UI stays unchanged:
//   {
//     success: true,
//     page_count: N,
//     pages: [{ overlay, base, width, height, stats }],
//     file_a_pages: number,
//     file_b_pages: number,
//   }
// `overlay` and `base` are base64-encoded PNGs (no data-URL prefix).

import * as pdfjsLib from 'pdfjs-dist';
import UTIF from 'utif';

// Point pdf.js at the worker copied into /public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MAX_PAGES = 20;
const PDF_RENDER_SCALE = 2; // preferred PDF render quality
const THRESHOLD = 250; // grayscale value below which a pixel is \"content\"

// Safety cap for the working pixel array. 8 megapixels ≈ 32 MB per RGBA buffer,
// and we hold ~4 of them concurrently (A, B, overlay, base) plus PNG encoding
// scratch. On mid-range devices that peaks around ~200 MB, which is safe.
const MAX_PIXELS_PER_PAGE = 8_000_000;

function getExt(name) {
  return (name || '').toLowerCase().split('.').pop();
}

// --------------------------------------------------------------------------
// Lazy per-file page loaders. Each returns { count, renderPage(i), cleanup() }.
// --------------------------------------------------------------------------

async function makePageLoader(file) {
  const ext = getExt(file.name);
  const buf = await file.arrayBuffer();

  if (ext === 'pdf') {
    const pdfTask = pdfjsLib.getDocument({ data: buf });
    const pdf = await pdfTask.promise;
    const count = Math.min(pdf.numPages, MAX_PAGES);
    return {
      count,
      async renderPage(i) {
        const page = await pdf.getPage(i + 1);
        // Start at preferred scale, shrink if the resulting canvas would blow
        // past the pixel budget.
        let viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
        const px = viewport.width * viewport.height;
        if (px > MAX_PIXELS_PER_PAGE) {
          const factor = Math.sqrt(MAX_PIXELS_PER_PAGE / px);
          viewport = page.getViewport({ scale: PDF_RENDER_SCALE * factor });
        }
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport, background: '#FFFFFF' }).promise;
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        // Immediately cleanup the pdf.js page (also releases operator list)
        page.cleanup();
        return data;
      },
      cleanup() {
        try {
          pdf.destroy();
        } catch (_) { /* noop */ }
      },
    };
  }

  if (ext === 'tif' || ext === 'tiff') {
    const ifds = UTIF.decode(buf);
    const count = Math.min(ifds.length, MAX_PAGES);
    return {
      count,
      async renderPage(i) {
        const ifd = ifds[i];
        UTIF.decodeImage(buf, ifd);
        const rgba = UTIF.toRGBA8(ifd); // Uint8Array RGBA
        const srcW = ifd.width;
        const srcH = ifd.height;

        // Free the raw decoded plane now that we have RGBA
        try { delete ifd.data; } catch (_) { /* noop */ }
        try { delete ifd.dataUnpacked; } catch (_) { /* noop */ }

        const srcPx = srcW * srcH;
        const needsDownscale = srcPx > MAX_PIXELS_PER_PAGE;
        const factor = needsDownscale ? Math.sqrt(MAX_PIXELS_PER_PAGE / srcPx) : 1;
        const dstW = Math.max(1, Math.floor(srcW * factor));
        const dstH = Math.max(1, Math.floor(srcH * factor));

        // Wrap without copying (shared buffer view)
        const rgbaClamped = new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, rgba.byteLength);

        const src = document.createElement('canvas');
        src.width = srcW;
        src.height = srcH;
        src.getContext('2d').putImageData(new ImageData(rgbaClamped, srcW, srcH), 0, 0);

        const dst = document.createElement('canvas');
        dst.width = dstW;
        dst.height = dstH;
        const dstCtx = dst.getContext('2d');
        dstCtx.fillStyle = '#FFFFFF';
        dstCtx.fillRect(0, 0, dstW, dstH);
        // drawImage composites over white and handles the downscale in one step
        dstCtx.drawImage(src, 0, 0, dstW, dstH);

        return dstCtx.getImageData(0, 0, dstW, dstH);
      },
      cleanup() { /* rgba refs go out of scope */ },
    };
  }

  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
    const url = URL.createObjectURL(new Blob([buf]));
    try {
      const bmp = await loadImage(url);
      let dstW = bmp.width;
      let dstH = bmp.height;
      const px = dstW * dstH;
      if (px > MAX_PIXELS_PER_PAGE) {
        const factor = Math.sqrt(MAX_PIXELS_PER_PAGE / px);
        dstW = Math.max(1, Math.floor(dstW * factor));
        dstH = Math.max(1, Math.floor(dstH * factor));
      }
      const canvas = document.createElement('canvas');
      canvas.width = dstW;
      canvas.height = dstH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, dstW, dstH);
      ctx.drawImage(bmp, 0, 0, dstW, dstH);
      const data = ctx.getImageData(0, 0, dstW, dstH);
      return {
        count: 1,
        async renderPage() {
          return data;
        },
        cleanup() { /* noop */ },
      };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  throw new Error(`Unsupported file format: ${ext}`);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });
}

// --------------------------------------------------------------------------
// Comparison of a single page pair. Aligns sizes by drawing each into a common
// white canvas, then classifies every pixel.
// --------------------------------------------------------------------------

function alignedImageData(imgData, width, height) {
  if (imgData.width === width && imgData.height === height) return imgData;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  const tmp = document.createElement('canvas');
  tmp.width = imgData.width;
  tmp.height = imgData.height;
  tmp.getContext('2d').putImageData(imgData, 0, 0);
  ctx.drawImage(tmp, 0, 0);
  return ctx.getImageData(0, 0, width, height);
}

async function canvasToBase64Png(canvas) {
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

async function comparePagePair(aImg, bImg) {
  const width = Math.max(aImg.width, bImg.width);
  const height = Math.max(aImg.height, bImg.height);

  const A = alignedImageData(aImg, width, height);
  const B = alignedImageData(bImg, width, height);

  const total = width * height;
  const overlay = new Uint8ClampedArray(total * 4);
  const base = new Uint8ClampedArray(total * 4);

  const aData = A.data;
  const bData = B.data;

  let overlapPx = 0;
  let onlyAPx = 0;
  let onlyBPx = 0;

  for (let i = 0; i < total; i++) {
    const p = i * 4;
    const ga = (aData[p] + aData[p + 1] + aData[p + 2]) / 3;
    const gb = (bData[p] + bData[p + 1] + bData[p + 2]) / 3;
    const hasA = ga < THRESHOLD;
    const hasB = gb < THRESHOLD;

    if (hasA && hasB) {
      overlay[p] = 0; overlay[p + 1] = 0; overlay[p + 2] = 0; overlay[p + 3] = 255;
      overlapPx++;
    } else if (hasA) {
      overlay[p] = 239; overlay[p + 1] = 68; overlay[p + 2] = 68; overlay[p + 3] = 255;
      onlyAPx++;
    } else if (hasB) {
      overlay[p] = 34; overlay[p + 1] = 197; overlay[p + 2] = 94; overlay[p + 3] = 255;
      onlyBPx++;
    }

    const combined = Math.min(ga, gb);
    const g = Math.round(200 + (combined / 255) * 55);
    base[p] = g; base[p + 1] = g; base[p + 2] = g; base[p + 3] = 255;
  }

  const overlayCanvas = document.createElement('canvas');
  overlayCanvas.width = width;
  overlayCanvas.height = height;
  overlayCanvas.getContext('2d').putImageData(new ImageData(overlay, width, height), 0, 0);

  const baseCanvas = document.createElement('canvas');
  baseCanvas.width = width;
  baseCanvas.height = height;
  baseCanvas.getContext('2d').putImageData(new ImageData(base, width, height), 0, 0);

  const [overlayB64, baseB64] = await Promise.all([
    canvasToBase64Png(overlayCanvas),
    canvasToBase64Png(baseCanvas),
  ]);

  return {
    overlay: overlayB64,
    base: baseB64,
    width,
    height,
    stats: { total, overlap: overlapPx, only_a: onlyAPx, only_b: onlyBPx },
  };
}

function blankImageData(width, height) {
  const arr = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < arr.length; i += 4) {
    arr[i] = 255; arr[i + 1] = 255; arr[i + 2] = 255; arr[i + 3] = 255;
  }
  return new ImageData(arr, width, height);
}

// --------------------------------------------------------------------------
// Public entry — orchestrates lazy page loading + pairwise comparison.
// --------------------------------------------------------------------------

export async function compareFilesLocal(fileA, fileB, onProgress) {
  if (!fileA || !fileB) throw new Error('Two files are required');

  onProgress && onProgress({ phase: 'decoding', message: 'Reading files…' });
  const [loaderA, loaderB] = await Promise.all([makePageLoader(fileA), makePageLoader(fileB)]);

  if (!loaderA.count || !loaderB.count) {
    loaderA.cleanup?.();
    loaderB.cleanup?.();
    throw new Error('Could not read pages from one of the files');
  }

  const originalA = loaderA.count;
  const originalB = loaderB.count;
  const maxPages = Math.max(loaderA.count, loaderB.count);

  const results = [];
  try {
    for (let i = 0; i < maxPages; i++) {
      onProgress && onProgress({
        phase: 'comparing',
        message: `Comparing page ${i + 1} of ${maxPages}…`,
        current: i + 1,
        total: maxPages,
      });
      // Yield to the UI thread before each heavy page
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 0));

      // Render just this page from each side
      // eslint-disable-next-line no-await-in-loop
      const imgA = i < loaderA.count ? await loaderA.renderPage(i) : null;
      // eslint-disable-next-line no-await-in-loop
      const imgB = i < loaderB.count ? await loaderB.renderPage(i) : null;

      const w = (imgA?.width ?? imgB?.width) || 1;
      const h = (imgA?.height ?? imgB?.height) || 1;

      const finalA = imgA || blankImageData(w, h);
      const finalB = imgB || blankImageData(w, h);

      // eslint-disable-next-line no-await-in-loop
      const res = await comparePagePair(finalA, finalB);
      results.push(res);
    }
  } finally {
    loaderA.cleanup?.();
    loaderB.cleanup?.();
  }

  return {
    success: true,
    page_count: maxPages,
    pages: results,
    file_a_pages: originalA,
    file_b_pages: originalB,
  };
}
